import {
  ProtocolCardChain,
  ProtocolCardIdentity,
} from '@/components/admin/protocoles/protocol-card';
import { FilterBar } from '@/components/admin/versioned/filter-bar';
import { FilterPanel } from '@/components/admin/versioned/filter-panel';
import { FilterSummary } from '@/components/admin/versioned/filter-summary';
import { VersionedGrid } from '@/components/admin/versioned/versioned-grid';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { duplicateProtocol, fetchProtocols, restoreProtocol } from '@/lib/api';
import {
  activeProtocolFilters,
  filterProtocols,
  hasActiveProtocolFilters,
  hasHiddenProtocolFilter,
  protocolAuthors,
  protocolCounts,
  protocolFiltersOf,
  protocolSearchOf,
  readProtocolFilters,
  resetProtocolFilters,
} from '@/lib/protocol-filters';
import { countLabel } from '@/lib/protocol-labels';
import { PROTOCOL_WORDING } from '@/lib/protocol-wording';
import { useVersionedDuplication } from '@/lib/versioned-duplication';
import { VERSIONED_SORT_LABEL, versionedRefusal, versionedRefusalOf } from '@/lib/versioned-labels';
import type { ProtocolCard, ProtocolFilters } from '@matter_extractor/shared';
import { protocolSortSchema } from '@matter_extractor/shared';
import { Alert, Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La bibliothèque de protocoles - the screen that replaces the couture of #32
 * (refonte modulaire §4, §7.1, §15; issue #49).
 *
 * The measure of the versioned layer, and the shortest of the three libraries:
 * a protocol contributes NO filter and NO sort of its own (§15), so the panel's
 * own slot is empty and the sort select offers the four common labels. What is
 * left for this file is the wording, the two card blocks of §7.1 - the
 * mini-chain and the warning line - and the two facets of the line of facts.
 *
 * ## The whole library in one read, filtered in the page
 *
 * `GET /api/protocols` serves every protocol, retired ones included, and the
 * filters of §4 are applied by `lib/versioned-filters.ts`. That decision belongs
 * to `protocolListSchema` and is argued there; what it buys this screen is that
 * a keystroke in « Nom » costs a `filter()` rather than a round trip.
 *
 * ## The filters live in the URL
 *
 * The régime of the two libraries before it: a filtered list is exactly what
 * somebody pastes into a team thread, and a reload must not throw away what an
 * operator typed. `validateSearch` NEVER throws - a typo in a pasted link
 * degrades to the neutral filter rather than to a blank screen.
 *
 * ## One cache key, one shape (issue #16)
 *
 * `['protocols', email]` holds a `ProtocolList` and nothing else. The editor
 * reads `['protocol', email, id]`, which is a different shape under a different
 * prefix - the trap of #16 is two components agreeing on a key and disagreeing
 * on what is under it, and `useQuery` is generic per component so nothing would
 * catch it.
 */

export const Route = createFileRoute('/admin/protocoles/')({
  validateSearch: readProtocolFilters,
  component: ProtocolesPage,
});

const INTRO =
  'La suite d’étapes qu’une expérience joue sur sa page protocole : à chaque étape une direction pour l’interviewer, des objectifs jugés par des critères, des règles générales et des conditions de sortie. Un protocole se versionne comme un critère et se gèle au démarrage d’une session.';

function ProtocolesPage() {
  const { email } = useAdminSession();
  const filters = protocolFiltersOf(Route.useSearch());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Is the « Plus de filtres » panel open? Component state and not a search
   * param, the régime the two libraries before it settled: it is a posture of
   * the reader and not a property of the list. It opens on its own when a link
   * arrives with one of the hidden filters armed.
   */
  const [expanded, setExpanded] = useState(() => hasHiddenProtocolFilter(filters));

  const protocols = useQuery({
    queryKey: ['protocols', email],
    queryFn: fetchProtocols,
    retry: false,
  });

  const rejected = useRejectOn401(protocols.error);

  const restore = useMutation({
    mutationFn: (card: ProtocolCard) => restoreProtocol(card.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['protocols', email] }),
  });

  /**
   * « Dupliquer », from the card's « … » menu (§4.2, issue #116).
   *
   * The mutation itself is the layer's (`lib/versioned-duplication.ts`): the
   * gesture is a route of the versioning FACTORY, and the four libraries call
   * it with the three things that differ - the API call, the list's cache key,
   * and the address of the copy's editor, which only this route can type
   * against the route tree.
   */
  const duplicate = useVersionedDuplication<ProtocolCard>({
    duplicate: duplicateProtocol,
    listKey: 'protocols',
    onCopy: (protocolId) =>
      void navigate({
        to: '/admin/protocoles/$protocolId',
        params: { protocolId },
        search: { copie: true },
      }),
  });

  /**
   * One dimension at a time, merged into the address bar - and REPLACED there,
   * never pushed: the « Nom » field filters as it is typed, so a push would
   * leave one history entry per keystroke.
   */
  const change = (patch: Partial<ProtocolFilters>) =>
    navigate({
      to: '/admin/protocoles',
      search: protocolSearchOf({ ...filters, ...patch }),
      replace: true,
    });

  const cards = protocols.data?.protocols ?? [];
  const counts = protocolCounts(cards, filters);
  const chips = activeProtocolFilters(filters);
  const refused = versionedRefusalOf(restore.error);
  const duplicateRefused = versionedRefusalOf(duplicate.error);

  return (
    <section className="space-y-5 pb-16">
      <header className="space-y-1">
        {/*
          `/^protocoles$/i` is what `admin-shell.spec.ts` and the door catalogue
          read: the couture carried this exact heading, and the real screen keeps
          it so those assertions never had a screen to chase.
        */}
        <h1 className="font-bold text-bleu-nuit text-h2">Protocoles</h1>
        <p className="max-w-[95ch] text-gray-600 text-small">{INTRO}</p>
      </header>

      <FilterBar
        wording={PROTOCOL_WORDING}
        filters={filters}
        authors={protocolAuthors(cards, filters)}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((was) => !was)}
        onChange={change}
      />

      {expanded ? (
        <FilterPanel
          wording={PROTOCOL_WORDING}
          filters={filters}
          // NOTHING, and that is §15: « aucun filtre ni tri propre ». The slot
          // takes a node, so « no own filters » is an absent node - the panel
          // then opens on the two periods, which is what it draws.
          ownFilters={null}
          retiredCount={counts.retired}
          onChange={change}
          onReset={() =>
            navigate({
              to: '/admin/protocoles',
              search: protocolSearchOf(resetProtocolFilters(filters)),
              replace: true,
            })
          }
          resetDisabled={!hasActiveProtocolFilters(filters)}
        />
      ) : null}

      <FilterSummary
        wording={PROTOCOL_WORDING}
        chips={chips}
        counts={counts}
        sort={filters.sort}
        sortOptions={protocolSortSchema.options}
        // The four common sorts and no more: a protocol adds none (§15).
        sortLabel={VERSIONED_SORT_LABEL}
        onChange={change}
      />

      {duplicate.isError ? (
        <div data-testid="protocoles-duplication-refusee">
          <Alert tone="danger">
            {duplicateRefused === null
              ? 'La copie n’a pas pu être écrite. Réessayez dans un instant.'
              : versionedRefusal(duplicateRefused, PROTOCOL_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      {restore.isError ? (
        <div data-testid="protocoles-restauration-refusee">
          <Alert tone="danger">
            {refused === null
              ? 'La restauration a échoué. Réessayez dans un instant.'
              : versionedRefusal(refused, PROTOCOL_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      {protocols.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement des protocoles" />
        </div>
      ) : null}

      {protocols.isError && !rejected ? (
        <p className="text-gray-700">La bibliothèque de protocoles n’a pas pu être chargée.</p>
      ) : null}

      {protocols.data ? (
        <VersionedGrid
          wording={PROTOCOL_WORDING}
          cards={filterProtocols(cards, filters)}
          // Why it is empty, worded HERE: only this screen knows whether a
          // filter is armed.
          emptyMessage={
            chips.length === 0
              ? 'Aucun protocole pour l’instant. La carte en pointillé ouvre l’éditeur vide.'
              : 'Aucun protocole ne répond à ces filtres. Retirez une pastille, ou réinitialisez.'
          }
          /*
            « Le nombre d'étapes et le nombre de sauts après l'usage » (§15). A
            protocol with no jump says NOTHING rather than « 0 saut »: the same
            rule the layer holds for a usage of zero - a zero teaches nothing and
            costs a fact on every linear protocol, which is most of them.
          */
          facetsOf={(card) => [
            countLabel(card.steps.length, 'étape'),
            ...(card.jumpCount === 0 ? [] : [countLabel(card.jumpCount, 'saut')]),
          ]}
          renderIdentity={(card) => <ProtocolCardIdentity card={card} />}
          renderExtra={(card) => <ProtocolCardChain card={card} />}
          renderCardLink={(card, props) => (
            <Link
              to="/admin/protocoles/$protocolId"
              params={{ protocolId: card.id }}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          renderCreateLink={(props) => (
            <Link
              to="/admin/protocoles/nouveau"
              data-testid={props['data-testid']}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          onRestore={(card) => restore.mutate(card)}
          restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
          onDuplicate={(card, tag) => duplicate.mutate({ card, tag })}
          duplicatingId={duplicate.isPending ? (duplicate.variables?.card.id ?? null) : null}
        />
      ) : null}
    </section>
  );
}
